import { AbstractControl, FormControl } from '@angular/forms';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { calculateTotalMonthlyOutgoing, calculateTotalMonthlyOutgoingsHKGIndustrial, RoundFn } from '@marketiq/calc-core';

/**
 * Space-terms auto-calculation orchestration, moved verbatim from the host
 * `auto-calculation-space-terms-section.ts`. The host keeps the two catalog/framework-coupled
 * concerns and supplies them to this binder:
 *   - CONTROL RESOLUTION (`findControlByFrameworkPattern`) — the host resolves every FormControl and
 *     passes the {@link SpaceTermsControls} bundle in.
 *   - UOM AUTO-SELECT from catalog options (`autoSelectFirstUOMOption`, which reads `column.values`) —
 *     injected as the {@link AutoSelectUomFn} callback; the resolved UOM columns are passed opaquely.
 *
 * Substitutions vs the host original (everything else verbatim — same debounce(100), same
 * dirty/touched gates, same focus-listeners, same cross-calc chaining, same per-calc rounding policy):
 *   1. `this.columns[X]` -> the opaque columns in {@link SpaceTermsUomColumns} (passed to the callback).
 *   2. `SpaceAutoCalculationHelper.patchValueIfChanged` -> the reproduced {@link patchValueIfChanged}.
 *   3. `SpaceAutoCalculationHelper.autoSelectFirstUOMOption(v,u,col)` -> `this.autoSelectUom(v,u,col)`.
 *   4. effective-rent rounding `formatNumber(v,'en','1.2-2')` + naive strip + `Math.round` -> the injected
 *      {@link RoundFn} (`autoCalcRound`). Both are 2dp; byte-equivalent for non-Poland and CORRECTS
 *      Poland (the host's naive `/[^0-9.-]/` strip corrupted pl-PL), consistent with the sale domain.
 *      asking/achievable/total write the RAW product (no rounding) exactly as the host.
 *
 * effective-rent uses the host's INLINE Formula A + its full null/0 gating (NOT the calc-core
 * `calculateEffectiveRent` kernel, whose guard semantics differ); total-monthly-outgoing uses the
 * calc-core `calculateTotalMonthlyOutgoing` kernel (already consumed by the host).
 */

/** Resolved space-terms FormControls (host resolves via its framework helper, passes them here). */
export interface SpaceTermsControls {
  achievableRent: FormControl | null;
  leaseTerm: FormControl | null;
  rentFree: FormControl | null;
  effectiveRent: FormControl | null;
  effectiveRentUom: FormControl | null;
  achievableRentUom: FormControl | null;
  askingRate: FormControl | null;
  availableSpace: FormControl | null;
  askingAmount: FormControl | null;
  askingAmountUom: FormControl | null;
  askingRateUom: FormControl | null;
  achievableAmount: FormControl | null;
  achievableAmountUom: FormControl | null;
  managementFee: FormControl | null;
  totalMonthlyOutgoing: FormControl | null;
  totalMonthlyOutgoingUom: FormControl | null;
  // HKG Industrial Total Monthly Outgoings
  totalMonthlyOutgoingsHKGIndustrial: FormControl | null;
  totalMonthlyOutgoingsHKGIndustrialUom: FormControl | null;
}

/** Resolved UOM columns (opaque to this binder — passed straight to {@link AutoSelectUomFn}). */
export interface SpaceTermsUomColumns {
  effectiveRentUom: unknown;
  askingAmountUom: unknown;
  achievableAmountUom: unknown;
  totalMonthlyOutgoingUom: unknown;
  // HKG Industrial Total Monthly Outgoings
  totalMonthlyOutgoingsHKGIndustrialUom: unknown;
}

/** Host `autoSelectFirstUOMOption(valueControl, uomControl, uomColumn)`. */
export type AutoSelectUomFn = (
  valueControl: FormControl | null,
  uomControl: FormControl | null,
  uomColumn: unknown
) => void;

/** Reproduces the host `SpaceAutoCalculationHelper.patchValueIfChanged` (error-clearing + dirty/pristine). */
function patchValueIfChanged(control: FormControl | null, newValue: unknown, markDirtyTouched = false): void {
  if (control && control.value !== newValue) {
    control.patchValue(newValue, { emitEvent: false });
    if (newValue === null || newValue === undefined) {
      control.setErrors(null);
      if (markDirtyTouched) {
        control.markAsDirty();
        control.markAsTouched();
      } else {
        control.markAsPristine();
        control.markAsUntouched();
      }
    } else if (markDirtyTouched) {
      control.setErrors(null);
      control.markAsDirty();
      control.markAsTouched();
    }
  }
}

function isEveryControlNotNull(controls: (AbstractControl | null)[]): boolean {
  return controls.every(ctrl => ctrl != null);
}

export class SpaceTermsAutoCalcBinder {
  private readonly destroy$ = new Subject<void>();
  private totalMonthlyOutgoingControls!: {
    effectiveRentCtrl: FormControl | null;
    managementFeeCtrl: FormControl | null;
    spaceSizeCtrl: FormControl | null;
    totalMonthlyOutgoingCtrl: FormControl | null;
    totalMonthlyOutgoingUomCtrl: FormControl | null;
    achievableRentCtrl: FormControl | null;
  };
  private totalMonthlyOutgoingsHKGIndustrialControls!: {
    effectiveRentCtrl: FormControl | null;
    managementFeeCtrl: FormControl | null;
    spaceSizeCtrl: FormControl | null;
    totalMonthlyOutgoingsCtrl: FormControl | null;
    totalMonthlyOutgoingsUomCtrl: FormControl | null;
    achievableRentCtrl: FormControl | null;
  };
  private uomColumns!: SpaceTermsUomColumns;

  /**
   * @param round host {@link RoundFn} (`autoCalcRound`) — used only for effective-rent.
   * @param autoSelectUom host `autoSelectFirstUOMOption` callback (reads catalog `column.values`).
   */
  constructor(private readonly round: RoundFn, private readonly autoSelectUom: AutoSelectUomFn) {}

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAutoCalcListeners(c: SpaceTermsControls, uomColumns: SpaceTermsUomColumns): Observable<unknown>[] {
    this.uomColumns = uomColumns;
    const listOfListeners: Observable<unknown>[] = [];

    const effectiveRentCalculation$ = this.setupEffectiveRentCalculation(
      c.achievableRent,
      c.leaseTerm,
      c.rentFree,
      c.effectiveRent,
      c.effectiveRentUom,
      c.achievableRentUom
    );
    if (effectiveRentCalculation$) {
      listOfListeners.push(effectiveRentCalculation$);
    }
    const askingRentCalculation$ = this.setupAskingRentCalculation(
      c.askingRate,
      c.availableSpace,
      c.askingAmount,
      c.askingAmountUom,
      c.askingRateUom
    );
    if (askingRentCalculation$) {
      listOfListeners.push(askingRentCalculation$);
    }
    const achievableRentCalculation$ = this.setupAchievableRentCalculation(
      c.achievableRent,
      c.availableSpace,
      c.achievableAmount,
      c.achievableAmountUom
    );
    if (achievableRentCalculation$) {
      listOfListeners.push(achievableRentCalculation$);
    }
    this.totalMonthlyOutgoingControls = {
      effectiveRentCtrl: c.effectiveRent,
      managementFeeCtrl: c.managementFee,
      spaceSizeCtrl: c.availableSpace,
      totalMonthlyOutgoingCtrl: c.totalMonthlyOutgoing,
      totalMonthlyOutgoingUomCtrl: c.totalMonthlyOutgoingUom,
      achievableRentCtrl: c.achievableRent,
    };
    const totalMonthlyOutgoingCalculation$ = this.setupTotalMonthlyOutgoingCalculation(
      this.totalMonthlyOutgoingControls.effectiveRentCtrl,
      this.totalMonthlyOutgoingControls.managementFeeCtrl,
      this.totalMonthlyOutgoingControls.spaceSizeCtrl,
      this.totalMonthlyOutgoingControls.totalMonthlyOutgoingCtrl,
      this.totalMonthlyOutgoingControls.totalMonthlyOutgoingUomCtrl,
      this.totalMonthlyOutgoingControls.achievableRentCtrl
    );
    if (totalMonthlyOutgoingCalculation$) {
      listOfListeners.push(totalMonthlyOutgoingCalculation$);
    }
    // HKG Industrial Total Monthly Outgoings
    this.totalMonthlyOutgoingsHKGIndustrialControls = {
      effectiveRentCtrl: c.effectiveRent,
      managementFeeCtrl: c.managementFee,
      spaceSizeCtrl: c.availableSpace,
      totalMonthlyOutgoingsCtrl: c.totalMonthlyOutgoingsHKGIndustrial,
      totalMonthlyOutgoingsUomCtrl: c.totalMonthlyOutgoingsHKGIndustrialUom,
      achievableRentCtrl: c.achievableRent,
    };
    const totalMonthlyOutgoingsHKGIndustrialCalculation$ = this.setupTotalMonthlyOutgoingsHKGIndustrialCalculation(
      this.totalMonthlyOutgoingsHKGIndustrialControls.effectiveRentCtrl,
      this.totalMonthlyOutgoingsHKGIndustrialControls.managementFeeCtrl,
      this.totalMonthlyOutgoingsHKGIndustrialControls.spaceSizeCtrl,
      this.totalMonthlyOutgoingsHKGIndustrialControls.totalMonthlyOutgoingsCtrl,
      this.totalMonthlyOutgoingsHKGIndustrialControls.totalMonthlyOutgoingsUomCtrl,
      this.totalMonthlyOutgoingsHKGIndustrialControls.achievableRentCtrl
    );
    if (totalMonthlyOutgoingsHKGIndustrialCalculation$) {
      listOfListeners.push(totalMonthlyOutgoingsHKGIndustrialCalculation$);
    }
    return listOfListeners;
  }

  private setupEffectiveRentCalculation(
    achievableRentCtrl: FormControl | null,
    leaseTermCtrl: FormControl | null,
    rentFreeCtrl: FormControl | null,
    effectiveRentCtrl: FormControl | null,
    effectiveRentUomCtrl: FormControl | null,
    achievableRentUomCtrl: FormControl | null
  ): Observable<unknown> | null {
    if (!isEveryControlNotNull([achievableRentCtrl, effectiveRentCtrl])) {
      return null;
    }
    this.addEffectiveRentFocusListeners(
      achievableRentCtrl!,
      leaseTermCtrl,
      rentFreeCtrl,
      effectiveRentCtrl!,
      effectiveRentUomCtrl,
      achievableRentUomCtrl
    );
    const observables: Observable<unknown>[] = [];
    // Do NOT use startWith - preserve API values on initial load
    if (achievableRentCtrl) {
      observables.push(achievableRentCtrl.valueChanges);
    }
    if (leaseTermCtrl) {
      observables.push(leaseTermCtrl.valueChanges);
    }
    if (rentFreeCtrl) {
      observables.push(rentFreeCtrl.valueChanges);
    }
    if (observables.length > 0) {
      return combineLatest(observables).pipe(
        debounceTime(100),
        tap(([achievableRent, leaseTerm, rentFree]) => {
          const dependentFieldsChanged =
            achievableRentCtrl?.dirty ||
            achievableRentCtrl?.touched ||
            leaseTermCtrl?.dirty ||
            leaseTermCtrl?.touched ||
            rentFreeCtrl?.dirty ||
            rentFreeCtrl?.touched;
          if (dependentFieldsChanged) {
            this.calculateEffectiveRent(
              achievableRent as number,
              leaseTerm as number,
              rentFree as number,
              effectiveRentCtrl,
              effectiveRentUomCtrl,
              achievableRentUomCtrl,
              true
            );
          }
        }),
        takeUntil(this.destroy$)
      );
    }
    return null;
  }

  private calculateEffectiveRent(
    achievableRent: number,
    leaseTerm: number,
    rentFree: number,
    effectiveRentCtrl: FormControl | null,
    effectiveRentUomCtrl: FormControl | null,
    achievableRentUomCtrl: FormControl | null,
    markDirtyTouched = false
  ): void {
    if (!effectiveRentCtrl) {
      return;
    }
    const hasAllRequiredValues =
      achievableRent != null &&
      achievableRent !== undefined &&
      achievableRent !== 0 &&
      leaseTerm != null &&
      leaseTerm !== undefined &&
      leaseTerm !== 0;
    if (!hasAllRequiredValues) {
      patchValueIfChanged(effectiveRentCtrl, null, markDirtyTouched);
      if (effectiveRentUomCtrl) {
        patchValueIfChanged(effectiveRentUomCtrl, null, markDirtyTouched);
      }
      this.triggerTotalMonthlyOutgoingCalculation();
      return;
    }
    try {
      const rentFreeValue = rentFree || 0;
      if (leaseTerm === 0 || achievableRent === 0) {
        patchValueIfChanged(effectiveRentCtrl, null, markDirtyTouched);
        if (effectiveRentUomCtrl) {
          patchValueIfChanged(effectiveRentUomCtrl, null, markDirtyTouched);
        }
        this.triggerTotalMonthlyOutgoingCalculation();
        return;
      }
      if (rentFreeValue >= leaseTerm) {
        patchValueIfChanged(effectiveRentCtrl, 0, markDirtyTouched);
        if (effectiveRentUomCtrl) {
          this.autoSelectUom(effectiveRentCtrl, effectiveRentUomCtrl, this.uomColumns.effectiveRentUom);
        }
        this.triggerTotalMonthlyOutgoingCalculation();
        return;
      }
      const effectiveRentValue = ((leaseTerm - rentFreeValue) / leaseTerm) * achievableRent;
      if (typeof effectiveRentValue !== 'number' || !isFinite(effectiveRentValue) || isNaN(effectiveRentValue)) {
        patchValueIfChanged(effectiveRentCtrl, null, markDirtyTouched);
        if (effectiveRentUomCtrl) {
          patchValueIfChanged(effectiveRentUomCtrl, null, markDirtyTouched);
        }
        this.triggerTotalMonthlyOutgoingCalculation();
        return;
      }
      // Host rounded via formatNumber(v,'en','1.2-2') + naive strip + Math.round(*100)/100 (2dp); the
      // injected RoundFn (autoCalcRound) is the same 2dp, Poland-corrected.
      const finalValue = this.round(effectiveRentValue);
      patchValueIfChanged(effectiveRentCtrl, finalValue, markDirtyTouched);
      if (effectiveRentUomCtrl) {
        this.autoSelectUom(effectiveRentCtrl, effectiveRentUomCtrl, this.uomColumns.effectiveRentUom);
      }
      this.triggerTotalMonthlyOutgoingCalculation();
    } catch (error) {
      patchValueIfChanged(effectiveRentCtrl, null, markDirtyTouched);
      if (effectiveRentUomCtrl) {
        patchValueIfChanged(effectiveRentUomCtrl, null, markDirtyTouched);
      }
      this.triggerTotalMonthlyOutgoingCalculation();
    }
  }

  private addEffectiveRentFocusListeners(
    achievableRentCtrl: FormControl,
    leaseTermCtrl: FormControl | null,
    rentFreeCtrl: FormControl | null,
    effectiveRentCtrl: FormControl,
    effectiveRentUomCtrl: FormControl | null,
    achievableRentUomCtrl: FormControl | null
  ): void {
    const triggerCalculation = () => {
      const controlsToCheck = [achievableRentCtrl, leaseTermCtrl, rentFreeCtrl].filter(Boolean);
      const shouldCalculate = controlsToCheck.some(control => control?.dirty || control?.touched);
      if (!shouldCalculate) {
        return;
      }
      const achievableRent = achievableRentCtrl.value;
      const leaseTerm = leaseTermCtrl?.value;
      const rentFree = rentFreeCtrl?.value;
      this.calculateEffectiveRent(
        achievableRent,
        leaseTerm,
        rentFree,
        effectiveRentCtrl,
        effectiveRentUomCtrl,
        achievableRentUomCtrl,
        true
      );
    };
    const controlsToWatch = [achievableRentCtrl, leaseTermCtrl, rentFreeCtrl].filter(Boolean);
    controlsToWatch.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => triggerCalculation());
      }
    });
  }

  private setupAskingRentCalculation(
    askingRateCtrl: FormControl | null,
    availableSpaceCtrl: FormControl | null,
    askingAmountCtrl: FormControl | null,
    askingAmountUomCtrl: FormControl | null,
    askingRateUomCtrl: FormControl | null
  ): Observable<unknown> | null {
    if (!askingRateCtrl || !availableSpaceCtrl || !askingAmountCtrl) {
      return null;
    }
    this.addAskingRentFocusListeners(askingRateCtrl, availableSpaceCtrl, askingAmountCtrl, askingAmountUomCtrl, askingRateUomCtrl);
    const askingRate$ = askingRateCtrl.valueChanges;
    const availableSpace$ = availableSpaceCtrl.valueChanges;
    return combineLatest([askingRate$, availableSpace$]).pipe(
      debounceTime(100),
      tap(([askingRate, availableSpace]) => {
        const dependentFieldsChanged =
          askingRateCtrl?.dirty || askingRateCtrl?.touched || availableSpaceCtrl?.dirty || availableSpaceCtrl?.touched;
        if (dependentFieldsChanged) {
          this.calculateAskingRentAmount(
            askingRate as number,
            availableSpace as number,
            askingAmountCtrl,
            askingAmountUomCtrl,
            askingRateUomCtrl,
            true
          );
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private calculateAskingRentAmount(
    askingRate: number,
    spaceSize: number,
    askingAmountCtrl: FormControl | null,
    askingAmountUomCtrl: FormControl | null,
    askingRateUomCtrl: FormControl | null,
    markDirtyTouched = false
  ): void {
    if (!askingAmountCtrl) {
      return;
    }
    if (!askingRate || askingRate <= 0) {
      patchValueIfChanged(askingAmountCtrl, null, markDirtyTouched);
      if (askingAmountUomCtrl) {
        patchValueIfChanged(askingAmountUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    if (!spaceSize || spaceSize <= 0) {
      patchValueIfChanged(askingAmountCtrl, null, markDirtyTouched);
      if (askingAmountUomCtrl) {
        patchValueIfChanged(askingAmountUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    try {
      const askingRentTotal = askingRate * spaceSize;
      patchValueIfChanged(askingAmountCtrl, askingRentTotal, markDirtyTouched);
      if (askingAmountUomCtrl) {
        this.autoSelectUom(askingAmountCtrl, askingAmountUomCtrl, this.uomColumns.askingAmountUom);
      }
    } catch (error) {
      patchValueIfChanged(askingAmountCtrl, null, markDirtyTouched);
      if (askingAmountUomCtrl) {
        patchValueIfChanged(askingAmountUomCtrl, null, markDirtyTouched);
      }
    }
  }

  private addAskingRentFocusListeners(
    askingRateCtrl: FormControl,
    availableSpaceCtrl: FormControl,
    askingAmountCtrl: FormControl,
    askingAmountUomCtrl: FormControl | null,
    askingRateUomCtrl: FormControl | null
  ): void {
    const triggerCalculation = () => {
      const controlsToCheck = [askingRateCtrl, availableSpaceCtrl];
      const shouldCalculate = controlsToCheck.some(control => control?.dirty || control?.touched);
      if (!shouldCalculate) {
        return;
      }
      const askingRate = askingRateCtrl.value;
      const spaceSize = availableSpaceCtrl.value;
      this.calculateAskingRentAmount(askingRate, spaceSize, askingAmountCtrl, askingAmountUomCtrl, askingRateUomCtrl, true);
    };
    const controlsToWatch = [askingRateCtrl, availableSpaceCtrl];
    controlsToWatch.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => triggerCalculation());
      }
    });
  }

  private setupAchievableRentCalculation(
    achievableRateCtrl: FormControl | null,
    availableSpaceCtrl: FormControl | null,
    achievableAmountCtrl: FormControl | null,
    achievableAmountUomCtrl: FormControl | null
  ): Observable<unknown> | null {
    if (!achievableRateCtrl || !availableSpaceCtrl || !achievableAmountCtrl) {
      return null;
    }
    this.addAchievableRentFocusListeners(achievableRateCtrl, availableSpaceCtrl, achievableAmountCtrl, achievableAmountUomCtrl);
    const achievableRate$ = achievableRateCtrl.valueChanges;
    const availableSpace$ = availableSpaceCtrl.valueChanges;
    return combineLatest([achievableRate$, availableSpace$]).pipe(
      debounceTime(100),
      tap(([achievableRate, availableSpace]) => {
        const dependentFieldsChanged =
          achievableRateCtrl?.dirty ||
          achievableRateCtrl?.touched ||
          availableSpaceCtrl?.dirty ||
          availableSpaceCtrl?.touched;
        if (dependentFieldsChanged) {
          this.calculateAchievableRentAmount(
            achievableRate as number,
            availableSpace as number,
            achievableAmountCtrl,
            achievableAmountUomCtrl,
            true
          );
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private calculateAchievableRentAmount(
    achievableRate: number,
    spaceSize: number,
    achievableAmountCtrl: FormControl | null,
    achievableAmountUomCtrl: FormControl | null,
    markDirtyTouched = false
  ): void {
    if (!achievableAmountCtrl) {
      return;
    }
    if (!achievableRate || achievableRate <= 0) {
      patchValueIfChanged(achievableAmountCtrl, null, markDirtyTouched);
      if (achievableAmountUomCtrl) {
        patchValueIfChanged(achievableAmountUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    if (!spaceSize || spaceSize <= 0) {
      patchValueIfChanged(achievableAmountCtrl, null, markDirtyTouched);
      if (achievableAmountUomCtrl) {
        patchValueIfChanged(achievableAmountUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    try {
      const achievableRentTotal = achievableRate * spaceSize;
      patchValueIfChanged(achievableAmountCtrl, achievableRentTotal, markDirtyTouched);
      if (achievableAmountUomCtrl) {
        this.autoSelectUom(achievableAmountCtrl, achievableAmountUomCtrl, this.uomColumns.achievableAmountUom);
      }
    } catch (error) {
      patchValueIfChanged(achievableAmountCtrl, null, markDirtyTouched);
      if (achievableAmountUomCtrl) {
        patchValueIfChanged(achievableAmountUomCtrl, null, markDirtyTouched);
      }
    }
  }

  private addAchievableRentFocusListeners(
    achievableRateCtrl: FormControl,
    availableSpaceCtrl: FormControl,
    achievableAmountCtrl: FormControl,
    achievableAmountUomCtrl: FormControl | null
  ): void {
    const triggerCalculation = () => {
      const controlsToCheck = [achievableRateCtrl, availableSpaceCtrl];
      const shouldCalculate = controlsToCheck.some(control => control?.dirty || control?.touched);
      if (!shouldCalculate) {
        return;
      }
      const achievableRate = achievableRateCtrl.value;
      const spaceSize = availableSpaceCtrl.value;
      this.calculateAchievableRentAmount(achievableRate, spaceSize, achievableAmountCtrl, achievableAmountUomCtrl, true);
    };
    const controlsToWatch = [achievableRateCtrl, availableSpaceCtrl];
    controlsToWatch.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => triggerCalculation());
      }
    });
  }

  private setupTotalMonthlyOutgoingCalculation(
    effectiveRentCtrl: FormControl | null,
    managementFeeCtrl: FormControl | null,
    spaceSizeCtrl: FormControl | null,
    totalMonthlyOutgoingCtrl: FormControl | null,
    totalMonthlyOutgoingUomCtrl: FormControl | null,
    achievableRentCtrl: FormControl | null
  ): Observable<unknown> | null {
    if (!isEveryControlNotNull([effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl, totalMonthlyOutgoingCtrl])) {
      return null;
    }
    this.addTotalMonthlyOutgoingFocusListeners(
      effectiveRentCtrl!,
      managementFeeCtrl!,
      spaceSizeCtrl!,
      totalMonthlyOutgoingCtrl!,
      totalMonthlyOutgoingUomCtrl,
      achievableRentCtrl
    );
    const observables: Observable<unknown>[] = [];
    // Do NOT use startWith - we don't want to calculate on initial load, only on actual changes
    observables.push(effectiveRentCtrl!.valueChanges);
    observables.push(managementFeeCtrl!.valueChanges);
    observables.push(spaceSizeCtrl!.valueChanges);
    if (achievableRentCtrl) {
      observables.push(achievableRentCtrl.valueChanges);
    }
    return merge(...observables).pipe(
      debounceTime(100),
      tap(() => {
        const dependentFieldsChanged =
          effectiveRentCtrl!.dirty ||
          effectiveRentCtrl!.touched ||
          managementFeeCtrl!.dirty ||
          managementFeeCtrl!.touched ||
          spaceSizeCtrl!.dirty ||
          spaceSizeCtrl!.touched ||
          achievableRentCtrl?.dirty ||
          achievableRentCtrl?.touched;
        if (dependentFieldsChanged) {
          const effectiveRent = effectiveRentCtrl!.value;
          const managementFee = managementFeeCtrl!.value;
          const spaceSize = spaceSizeCtrl!.value;
          const achievableRent = achievableRentCtrl?.value;
          this.calculateTotalMonthlyOutgoing(
            effectiveRent,
            managementFee,
            spaceSize,
            totalMonthlyOutgoingCtrl,
            totalMonthlyOutgoingUomCtrl,
            achievableRent,
            true
          );
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private calculateTotalMonthlyOutgoing(
    effectiveRent: number,
    managementFee: number,
    spaceSize: number,
    totalMonthlyOutgoingCtrl: FormControl | null,
    totalMonthlyOutgoingUomCtrl: FormControl | null,
    achievableRent?: number,
    markDirtyTouched = false
  ): void {
    if (!totalMonthlyOutgoingCtrl) {
      return;
    }
    const hasRequiredValues =
      spaceSize != null &&
      spaceSize !== undefined &&
      spaceSize > 0;
    if (!hasRequiredValues) {
      patchValueIfChanged(totalMonthlyOutgoingCtrl, null, markDirtyTouched);
      if (totalMonthlyOutgoingUomCtrl) {
        patchValueIfChanged(totalMonthlyOutgoingUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    try {
      const safeEffectiveRent = effectiveRent ?? 0;
      const safeManagementFee = managementFee ?? 0;
      const totalMonthlyOutgoing = calculateTotalMonthlyOutgoing(safeEffectiveRent, safeManagementFee, spaceSize, achievableRent);
      patchValueIfChanged(totalMonthlyOutgoingCtrl, totalMonthlyOutgoing, markDirtyTouched);
      if (totalMonthlyOutgoingUomCtrl) {
        this.autoSelectUom(totalMonthlyOutgoingCtrl, totalMonthlyOutgoingUomCtrl, this.uomColumns.totalMonthlyOutgoingUom);
      }
    } catch (error) {
      patchValueIfChanged(totalMonthlyOutgoingCtrl, null, markDirtyTouched);
      if (totalMonthlyOutgoingUomCtrl) {
        patchValueIfChanged(totalMonthlyOutgoingUomCtrl, null, markDirtyTouched);
      }
    }
  }

  private addTotalMonthlyOutgoingFocusListeners(
    effectiveRentCtrl: FormControl,
    managementFeeCtrl: FormControl,
    spaceSizeCtrl: FormControl,
    totalMonthlyOutgoingCtrl: FormControl,
    totalMonthlyOutgoingUomCtrl: FormControl | null,
    achievableRentCtrl: FormControl | null
  ): void {
    const triggerCalculation = () => {
      const controlsToCheck = [effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl];
      if (achievableRentCtrl) {
        controlsToCheck.push(achievableRentCtrl);
      }
      const shouldCalculate = controlsToCheck.some(control => control?.dirty || control?.touched);
      if (!shouldCalculate) {
        return;
      }
      const effectiveRent = effectiveRentCtrl.value;
      const managementFee = managementFeeCtrl.value;
      const spaceSize = spaceSizeCtrl.value;
      const achievableRent = achievableRentCtrl?.value;
      this.calculateTotalMonthlyOutgoing(
        effectiveRent,
        managementFee,
        spaceSize,
        totalMonthlyOutgoingCtrl,
        totalMonthlyOutgoingUomCtrl,
        achievableRent,
        true
      );
    };
    const controlsToWatch = [effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl];
    if (achievableRentCtrl) {
      controlsToWatch.push(achievableRentCtrl);
    }
    controlsToWatch.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => triggerCalculation());
      }
    });
  }

  private triggerTotalMonthlyOutgoingCalculation(): void {
    if (
      this.totalMonthlyOutgoingControls.effectiveRentCtrl &&
      this.totalMonthlyOutgoingControls.managementFeeCtrl &&
      this.totalMonthlyOutgoingControls.spaceSizeCtrl &&
      this.totalMonthlyOutgoingControls.totalMonthlyOutgoingCtrl
    ) {
      const effectiveRent = this.totalMonthlyOutgoingControls.effectiveRentCtrl.value;
      const managementFee = this.totalMonthlyOutgoingControls.managementFeeCtrl.value;
      const spaceSize = this.totalMonthlyOutgoingControls.spaceSizeCtrl.value;
      const achievableRent = this.totalMonthlyOutgoingControls.achievableRentCtrl?.value;
      this.calculateTotalMonthlyOutgoing(
        effectiveRent,
        managementFee,
        spaceSize,
        this.totalMonthlyOutgoingControls.totalMonthlyOutgoingCtrl,
        this.totalMonthlyOutgoingControls.totalMonthlyOutgoingUomCtrl,
        achievableRent,
        true
      );
    }
    // Also trigger HKG Industrial calculation when effective rent changes
    this.triggerTotalMonthlyOutgoingsHKGIndustrialCalculation();
  }

  private setupTotalMonthlyOutgoingsHKGIndustrialCalculation(
    effectiveRentCtrl: FormControl | null,
    managementFeeCtrl: FormControl | null,
    spaceSizeCtrl: FormControl | null,
    totalMonthlyOutgoingsCtrl: FormControl | null,
    totalMonthlyOutgoingsUomCtrl: FormControl | null,
    achievableRentCtrl: FormControl | null
  ): Observable<unknown> | null {
    if (!isEveryControlNotNull([effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl, totalMonthlyOutgoingsCtrl])) {
      return null;
    }
    this.addTotalMonthlyOutgoingsHKGIndustrialFocusListeners(
      effectiveRentCtrl!,
      managementFeeCtrl!,
      spaceSizeCtrl!,
      totalMonthlyOutgoingsCtrl!,
      totalMonthlyOutgoingsUomCtrl,
      achievableRentCtrl
    );
    const observables: Observable<unknown>[] = [];
    observables.push(effectiveRentCtrl!.valueChanges);
    observables.push(managementFeeCtrl!.valueChanges);
    observables.push(spaceSizeCtrl!.valueChanges);
    if (achievableRentCtrl) {
      observables.push(achievableRentCtrl.valueChanges);
    }
    return merge(...observables).pipe(
      debounceTime(100),
      tap(() => {
        const dependentFieldsChanged =
          effectiveRentCtrl!.dirty ||
          effectiveRentCtrl!.touched ||
          managementFeeCtrl!.dirty ||
          managementFeeCtrl!.touched ||
          spaceSizeCtrl!.dirty ||
          spaceSizeCtrl!.touched ||
          achievableRentCtrl?.dirty ||
          achievableRentCtrl?.touched;
        if (dependentFieldsChanged) {
          const effectiveRent = effectiveRentCtrl!.value;
          const managementFee = managementFeeCtrl!.value;
          const spaceSize = spaceSizeCtrl!.value;
          const achievableRent = achievableRentCtrl?.value;
          this.calculateTotalMonthlyOutgoingsHKGIndustrial(
            effectiveRent,
            managementFee,
            spaceSize,
            totalMonthlyOutgoingsCtrl,
            totalMonthlyOutgoingsUomCtrl,
            achievableRent,
            true
          );
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private calculateTotalMonthlyOutgoingsHKGIndustrial(
    effectiveRent: number,
    managementFee: number,
    spaceSize: number,
    totalMonthlyOutgoingsCtrl: FormControl | null,
    totalMonthlyOutgoingsUomCtrl: FormControl | null,
    achievableRent?: number,
    markDirtyTouched = false
  ): void {
    if (!totalMonthlyOutgoingsCtrl) {
      return;
    }
    const hasRequiredValues =
      spaceSize != null &&
      spaceSize !== undefined &&
      spaceSize > 0;
    if (!hasRequiredValues) {
      patchValueIfChanged(totalMonthlyOutgoingsCtrl, null, markDirtyTouched);
      if (totalMonthlyOutgoingsUomCtrl) {
        patchValueIfChanged(totalMonthlyOutgoingsUomCtrl, null, markDirtyTouched);
      }
      return;
    }
    try {
      const safeEffectiveRent = effectiveRent ?? 0;
      const safeManagementFee = managementFee ?? 0;
      const totalMonthlyOutgoings = calculateTotalMonthlyOutgoingsHKGIndustrial(
        safeEffectiveRent,
        safeManagementFee,
        spaceSize,
        achievableRent
      );
      patchValueIfChanged(totalMonthlyOutgoingsCtrl, totalMonthlyOutgoings, markDirtyTouched);
      if (totalMonthlyOutgoingsUomCtrl) {
        this.autoSelectUom(
          totalMonthlyOutgoingsCtrl,
          totalMonthlyOutgoingsUomCtrl,
          this.uomColumns.totalMonthlyOutgoingsHKGIndustrialUom
        );
      }
    } catch (error) {
      patchValueIfChanged(totalMonthlyOutgoingsCtrl, null, markDirtyTouched);
      if (totalMonthlyOutgoingsUomCtrl) {
        patchValueIfChanged(totalMonthlyOutgoingsUomCtrl, null, markDirtyTouched);
      }
    }
  }

  private addTotalMonthlyOutgoingsHKGIndustrialFocusListeners(
    effectiveRentCtrl: FormControl,
    managementFeeCtrl: FormControl,
    spaceSizeCtrl: FormControl,
    totalMonthlyOutgoingsCtrl: FormControl,
    totalMonthlyOutgoingsUomCtrl: FormControl | null,
    achievableRentCtrl: FormControl | null
  ): void {
    const triggerCalculation = () => {
      const controlsToCheck = [effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl];
      if (achievableRentCtrl) {
        controlsToCheck.push(achievableRentCtrl);
      }
      const shouldCalculate = controlsToCheck.some(control => control?.dirty || control?.touched);
      if (!shouldCalculate) {
        return;
      }
      const effectiveRent = effectiveRentCtrl.value;
      const managementFee = managementFeeCtrl.value;
      const spaceSize = spaceSizeCtrl.value;
      const achievableRent = achievableRentCtrl?.value;
      this.calculateTotalMonthlyOutgoingsHKGIndustrial(
        effectiveRent,
        managementFee,
        spaceSize,
        totalMonthlyOutgoingsCtrl,
        totalMonthlyOutgoingsUomCtrl,
        achievableRent,
        true
      );
    };
    const controlsToWatch = [effectiveRentCtrl, managementFeeCtrl, spaceSizeCtrl];
    if (achievableRentCtrl) {
      controlsToWatch.push(achievableRentCtrl);
    }
    controlsToWatch.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => triggerCalculation());
      }
    });
  }

  private triggerTotalMonthlyOutgoingsHKGIndustrialCalculation(): void {
    if (
      this.totalMonthlyOutgoingsHKGIndustrialControls?.effectiveRentCtrl &&
      this.totalMonthlyOutgoingsHKGIndustrialControls.managementFeeCtrl &&
      this.totalMonthlyOutgoingsHKGIndustrialControls.spaceSizeCtrl &&
      this.totalMonthlyOutgoingsHKGIndustrialControls.totalMonthlyOutgoingsCtrl
    ) {
      const effectiveRent = this.totalMonthlyOutgoingsHKGIndustrialControls.effectiveRentCtrl.value;
      const managementFee = this.totalMonthlyOutgoingsHKGIndustrialControls.managementFeeCtrl.value;
      const spaceSize = this.totalMonthlyOutgoingsHKGIndustrialControls.spaceSizeCtrl.value;
      const achievableRent = this.totalMonthlyOutgoingsHKGIndustrialControls.achievableRentCtrl?.value;
      this.calculateTotalMonthlyOutgoingsHKGIndustrial(
        effectiveRent,
        managementFee,
        spaceSize,
        this.totalMonthlyOutgoingsHKGIndustrialControls.totalMonthlyOutgoingsCtrl,
        this.totalMonthlyOutgoingsHKGIndustrialControls.totalMonthlyOutgoingsUomCtrl,
        achievableRent,
        true
      );
    }
  }
}
